import { useId, useRef, useState } from "react";
import { Icon } from "@/shared/ui";
import { agoText } from "../residentView";
import { eventText, type WorldFeed, type WorldFeedItem } from "./worldFeed";

/**
 * 观察室的实时事件流（星球上的新鲜事）：贴在地图底边、可拉起的面板。
 * - 收起时只露一条“最新一件事”（一行，不挡星球）；点一下拉起列表（地图下半截，地图上半截照样能看能点），再点收起；
 * - 一条事件一句中文（./worldFeed 的 eventText），时间按服务器时间说“3 分钟前”；
 * - 有片区、或涉及的居民在地图上有公开位置时，这一行能点：面板收起，交给页面（回到发生地 / 打开短卡；关了焦点回到底边那一条）；
 * - 读着、读不到（能重试）、这会儿很安静，分开说；不编事件。
 * 新的一件事到了，读屏会礼貌地念一句（aria-live）。
 * 拉起的时候告诉地图（onOpen）：地图把开着的卡、名单收起，列表不压在卡后面（117-2b 评审 R5 中：键盘拉起时鼠标那条「点外面关」走不到）。
 * 只给 /world 用（登录前后都是这一条）；「先逛逛」（?view=guest）的是「今天星球上发生的事」上拉面板（./DailySheet＋./GuestWorldStories，UI-WORLD-LIVING-117-1），原来给访客的 story 那一套随之删掉。
 */
export function WorldFeedPanel({
  feed,
  pending,
  failed,
  onRetry,
  canPick,
  onPick,
  nameOf,
  onOpen,
}: {
  feed: WorldFeed | undefined;
  pending: boolean;
  failed: boolean;
  onRetry: () => void;
  canPick: (item: WorldFeedItem) => boolean;
  onPick: (item: WorldFeedItem, from: HTMLElement) => void;
  /** 名字查表（后端 text 是空的时才用得上） */
  nameOf: (petId: string) => string | null;
  /** 拉起列表的那一下（收起时不叫） */
  onOpen?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const stripRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const items = feed?.items ?? [];
  const latest = items[0] ?? null;
  const serverNow = feed ? Date.parse(feed.server_time) : Number.NaN;
  const nowMs = Number.isFinite(serverNow) ? serverNow : Date.now();
  const line = pending ? "正在看星球上的新鲜事…" : failed ? "暂时没读到星球上的新鲜事" : latest ? eventText(latest, nameOf) : "这会儿星球上很安静";
  return (
    <section className={`ps-pmap-feed${open ? " is-open" : ""}`} aria-label="星球上的新鲜事" data-map-interactive data-testid="world-feed" data-state={pending ? "pending" : failed ? "failed" : items.length ? "ready" : "empty"}>
      <p className="visually-hidden" aria-live="polite">
        {latest && !failed ? `最新：${eventText(latest, nameOf)}` : ""}
      </p>
      {open ? (
        <div id={panelId} className="ps-pmap-feed__panel">
          <p className="ps-pmap-feed__title">星球上的新鲜事</p>
          {failed ? (
            <div className="ps-pmap-feed__empty">
              <p>暂时没读到星球上的新鲜事，等一下再试试。</p>
              <button type="button" className="ps-pmap-feed__retry" onClick={onRetry}>
                重试
              </button>
            </div>
          ) : pending ? (
            <p className="ps-pmap-feed__empty">正在看星球上的新鲜事…</p>
          ) : items.length ? (
            <ol className="ps-pmap-feed__list">
              {items.map((item) => {
                const text = eventText(item, nameOf);
                const ago = agoText(item.at, nowMs);
                return (
                  <li key={item.event_id}>
                    {canPick(item) ? (
                      <button
                        type="button"
                        className="ps-pmap-feed__row"
                        onClick={() => {
                          // 收起面板再出短卡（不叠两层）；短卡关了焦点回到底边那一条
                          setOpen(false);
                          onPick(item, stripRef.current ?? document.body);
                        }}
                      >
                        <span>{text}</span>
                        <time dateTime={item.at}>{ago}</time>
                      </button>
                    ) : (
                      <p className="ps-pmap-feed__row">
                        <span>{text}</span>
                        <time dateTime={item.at}>{ago}</time>
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="ps-pmap-feed__empty">这会儿星球上很安静，有新鲜事会出现在这里。</p>
          )}
        </div>
      ) : null}
      <button ref={stripRef} type="button" className="ps-pmap-feed__strip" aria-expanded={open} aria-controls={open ? panelId : undefined} onClick={() => { if (!open) onOpen?.(); setOpen(!open); }}>
        <span className="ps-pmap-feed__badge" aria-hidden="true">
          最新
        </span>
        <span className="ps-pmap-feed__line">{line}</span>
        {latest && !failed && !pending ? <time dateTime={latest.at}>{agoText(latest.at, nowMs)}</time> : null}
        <span className="visually-hidden">{open ? "，收起新鲜事" : "，看看星球上的新鲜事"}</span>
        <span className={`ps-pmap-feed__chev${open ? " is-open" : ""}`} aria-hidden="true">
          <Icon name="chevron" size={16} />
        </span>
      </button>
    </section>
  );
}
